import { useCallback, useEffect, useMemo, useState } from "react";
import { Crown, Loader2, Medal, MessageSquare, Star, Trophy, UserRound } from "lucide-react";
import PublicSiteLayout, { PageHero, PublicLinkButton, PUBLIC_LINKS } from "../components/PublicSiteLayout";
import { api, BACKEND_URL } from "../lib/api";
import { currencyLabel, visibleCurrencies } from "../lib/currencyDisplay";
import ProfileBadgeStrip from "../components/ProfileBadgeStrip";

export default function Leaderboard() {
  const [sortBy, setSortBy] = useState("free_currency");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    setError("");
    try {
      const { data } = await api.get(`/leaderboard?sort_by=${encodeURIComponent(sortBy)}`);
      setData(data);
    } catch (err) {
      setError(err.response?.data?.detail || "Could not load the leaderboard right now.");
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [sortBy]);

  useEffect(() => {
    load();
    const id = setInterval(() => load({ quiet: true }), 10000);
    return () => clearInterval(id);
  }, [load]);

  const currencies = useMemo(() => visibleCurrencies(data?.currencies || []), [data]);
  const activeCurrency = currencies.find((currency) => currency.id === sortBy) || currencies[0] || null;
  const viewers = Array.isArray(data?.viewers) ? data.viewers : [];
  const topViewers = viewers.slice(0, 3);
  const activeLabel = sortBy === "watchtime" ? "Active Watchtime" : currencyLabel(activeCurrency);

  return (
    <PublicSiteLayout>
      <div className="leaderboard-page">
      <PageHero
        eyebrow="Community leaderboard"
        title="Ratterday rankings."
        body="See who is showing up, earning currency, and climbing the community board. Rankings update as the platform records activity."
        actions={(
          <>
            <PublicLinkButton href="/redeems" primary>
              <Trophy className="w-4 h-4" /> Use Redeems
            </PublicLinkButton>
            <PublicLinkButton href={PUBLIC_LINKS.discord}>
              <MessageSquare className="w-4 h-4" /> Join Discord
            </PublicLinkButton>
          </>
        )}
      />

      <section className="leaderboard-controls">
        <div className="flex flex-col xl:flex-row xl:items-end xl:justify-between gap-4">
          <div>
            <div className="label-eyebrow text-[hsl(var(--currency-event))]">Now sorting by</div>
            <h2 className="mt-1 text-2xl sm:text-3xl font-black uppercase tracking-normal text-[hsl(var(--foreground))] flex items-center gap-2">
              <Trophy className="w-6 h-6" style={{ color: activeCurrency?.color || "hsl(var(--currency-event))" }} />
              {activeLabel}
            </h2>
            <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))]">
              Switch between currency rankings and active watchtime to see different slices of community activity.
            </p>
          </div>

          <div className="flex flex-wrap gap-2" data-testid="sort-tabs">
            {currencies.map((currency) => (
              <button
                key={currency.id}
                type="button"
                onClick={() => setSortBy(currency.id)}
                aria-pressed={sortBy === currency.id}
                data-testid={`sort-${currency.id}`}
                className="leaderboard-filter"
                style={{ "--ranking-color": currency.color || "var(--public-accent)" }}
              >
                {currencyLabel(currency)}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setSortBy("watchtime")}
              aria-pressed={sortBy === "watchtime"}
              data-testid="sort-watchtime"
              className="leaderboard-filter"
            >
              Active Watchtime
            </button>
          </div>
        </div>
      </section>

      {error && (
        <section className="mt-6 rounded border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-100">
          {error}
        </section>
      )}

      {loading ? (
        <section className="mt-8 surface bg-black/75 p-6 text-sm text-[hsl(var(--muted-foreground))] flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading leaderboard...
        </section>
      ) : viewers.length === 0 ? (
        <EmptyLeaderboard />
      ) : (
        <>
          <section className="leaderboard-podium" aria-label="Top performers">
            {topViewers.map((viewer, index) => (
              <TopViewerCard
                key={viewer.twitch_user_id || viewer.login || index}
                viewer={viewer}
                rank={index + 1}
                sortBy={sortBy}
                activeCurrency={activeCurrency}
              />
            ))}
          </section>

          <section className="leaderboard-standings">
            <div className="pb-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div>
                <div className="label-eyebrow text-[hsl(var(--currency-event))]">Full board</div>
                <h2 id="standings-title" className="text-xl font-semibold text-[hsl(var(--foreground))] mt-1">Community standings</h2>
              </div>
              <div className="text-xs text-[hsl(var(--muted-foreground))]">
                Updates automatically while this page is open.
              </div>
            </div>

            <div className="leaderboard-table-scroll" tabIndex={0} role="region" aria-label="Community standings, scroll for all currencies">
              <table className="leaderboard-table">
                <caption className="sr-only">Community standings sorted by {activeLabel}</caption>
                <thead className="text-left text-xs uppercase tracking-wider text-[hsl(var(--muted-foreground))]">
                  <tr className="border-b border-[hsl(var(--border))]">
                    <th scope="col" className="py-3 px-4 w-12">#</th>
                    <th scope="col">Viewer</th>
                    {currencies.map((currency) => (
                      <th scope="col" key={currency.id} className="text-right min-w-36" style={{ color: currency.color }}>
                        {currencyLabel(currency)}
                      </th>
                    ))}
                    <th scope="col" className="text-right pr-4">Active Watchtime</th>
                  </tr>
                </thead>
                <tbody>
                  {viewers.map((viewer, index) => (
                    <tr key={viewer.twitch_user_id || viewer.login || index} className="border-t border-[hsl(var(--border))] hover:bg-[hsl(var(--surface-2))]">
                      <td className="py-3 px-4 font-mono text-[hsl(var(--muted-foreground))]">{index + 1}</td>
                      <th scope="row" className="text-left font-normal">
                        <ViewerIdentity viewer={viewer} />
                      </th>
                      {currencies.map((currency) => (
                        <td key={currency.id} className="text-right font-mono" style={{ color: currency.color }}>
                          {Number(viewer.balances?.[currency.id] || 0).toLocaleString()}
                        </td>
                      ))}
                      <td className="text-right pr-4 font-mono text-[hsl(var(--muted-foreground))]">
                        {formatWatchtime(viewer.watchtime_minutes)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <ol className="leaderboard-mobile-list" aria-labelledby="standings-title">
              {viewers.map((viewer, index) => (
                <li key={viewer.twitch_user_id || viewer.login || index}>
                  <div className="leaderboard-mobile-identity"><span className="font-mono text-sm text-[hsl(var(--muted-foreground))]">#{index + 1}</span><ViewerIdentity viewer={viewer} /></div>
                  <dl className="leaderboard-mobile-stats">
                    {currencies.map((currency) => (
                      <div key={currency.id} className={sortBy === currency.id ? "is-selected" : ""}>
                        <dt><span className="ranking-currency-dot" style={{ background: currency.color }} />{currencyLabel(currency)}</dt>
                        <dd>{Number(viewer.balances?.[currency.id] || 0).toLocaleString()}</dd>
                      </div>
                    ))}
                    <div className={sortBy === "watchtime" ? "is-selected" : ""}><dt>Active Watchtime</dt><dd>{formatWatchtime(viewer.watchtime_minutes)}</dd></div>
                  </dl>
                </li>
              ))}
            </ol>
          </section>

          <section className="mt-6 text-sm text-[hsl(var(--muted-foreground))]">
            Active watchtime counts viewers who have chatted recently while the stream is live. Silent lurking is not counted yet.
          </section>
        </>
      )}
      </div>
    </PublicSiteLayout>
  );
}

function TopViewerCard({ viewer, rank, sortBy, activeCurrency }) {
  const score = sortBy === "watchtime"
    ? formatWatchtime(viewer.watchtime_minutes)
    : `${Number(viewer.balances?.[activeCurrency?.id] || 0).toLocaleString()} ${currencyLabel(activeCurrency)}`;
  const RankIcon = rank === 1 ? Crown : rank === 2 ? Trophy : Medal;

  return (
    <article className={`leaderboard-top-card${rank === 1 ? " is-first" : ""}`}>
      <div className="flex items-start justify-between gap-3">
        <ViewerIdentity viewer={viewer} large />
        <div className="leaderboard-rank">
          <RankIcon className="w-4 h-4 text-[hsl(var(--currency-event))]" /> #{rank}
        </div>
      </div>
      <div className="mt-5">
        <div className="label-eyebrow">{rank === 1 ? "Leading the board" : "Current score"}</div>
        <div className="leaderboard-score">{score}</div>
      </div>
    </article>
  );
}

function ViewerIdentity({ viewer, large = false }) {
  const displayName = viewer.display_name || viewer.login || "Viewer";
  const avatarUrl = resolveMediaUrl(viewer.profile_image_url);
  const accent = viewer.accent_color || "hsl(var(--currency-event))";
  const activeTitle = viewer.active_title;
  const badges = Array.isArray(viewer.featured_badges) ? viewer.featured_badges : [];
  return (
    <div className="leaderboard-identity flex items-center gap-3 min-w-0">
      {avatarUrl ? (
        <img
          src={avatarUrl}
          alt=""
          className={`${large ? "w-12 h-12" : "w-8 h-8"} rounded object-cover border shrink-0`}
          style={{ borderColor: accent }}
        />
      ) : (
        <span className={`${large ? "w-12 h-12" : "w-8 h-8"} rounded bg-[hsl(var(--currency-event))]/20 inline-flex items-center justify-center border shrink-0`} style={{ borderColor: accent }}>
          <UserRound className={large ? "w-6 h-6" : "w-4 h-4"} />
        </span>
      )}
      <span className="min-w-0">
        <span className="flex items-center gap-1.5 min-w-0">
          <span className={`block truncate ${large ? "text-lg font-semibold" : "font-medium"}`} style={{ color: accent }}>{displayName}</span>
          <ProfileBadgeStrip badges={badges} max={large ? 3 : 2} />
        </span>
        {activeTitle && (
          <span className="mt-0.5 flex items-center gap-1 truncate text-xs text-[hsl(var(--foreground))]">
            <Star className="w-3 h-3 shrink-0" style={{ color: activeTitle.color || accent }} />
            {activeTitle.name}
          </span>
        )}
        {viewer.login && (
          <span className="block truncate text-xs text-[hsl(var(--muted-foreground))]">@{viewer.login}</span>
        )}
      </span>
    </div>
  );
}

function resolveMediaUrl(value) {
  if (!value) return "";
  if (/^https?:\/\//i.test(value)) return value;
  if (value.startsWith("/")) return `${BACKEND_URL}${value}`;
  return value;
}

function EmptyLeaderboard() {
  return (
    <section className="mt-8 surface bg-black/75 p-6">
      <div className="label-eyebrow text-[hsl(var(--currency-event))]">No rankings yet</div>
      <h2 className="mt-1 text-2xl font-semibold text-[hsl(var(--foreground))]">The board is waiting for activity.</h2>
      <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))] max-w-2xl">
        Viewer stats will appear here once community activity starts coming through the platform.
      </p>
      <div className="mt-5 flex flex-wrap gap-3">
        <PublicLinkButton href="/redeems" primary>Browse Redeems</PublicLinkButton>
        <PublicLinkButton href={PUBLIC_LINKS.discord}>Join Discord</PublicLinkButton>
      </div>
    </section>
  );
}

function formatWatchtime(minutes) {
  const total = Math.max(0, Number(minutes) || 0);
  const hours = Math.floor(total / 60);
  const mins = total % 60;
  if (hours <= 0) return `${mins}m`;
  if (mins <= 0) return `${hours}h`;
  return `${hours}h ${mins}m`;
}
